/**
 * For premium users to manage their subscription. Allows them to switch plans and renewal periods.
 */
import * as preact from "preact";

import StripeCustomer from "StripeCustomer";
import * as genericModals from "genericModals";
import models from "models/models";
import * as subscriptionPlans from "shared/subscriptionPlans";
import * as utils from "shared/utils";

interface Props {}

type State =
  | {
      status: "loading";
    }
  | { status: "loaded"; cardString: string };

class ManageSubscriptionView extends preact.Component<Props, State> {
  stripeCustomer: any;

  constructor() {
    super();

    this.loadStripeCustomer();
  }

  loadStripeCustomer() {
    this.state = {
      status: "loading",
    };

    this.stripeCustomer = new StripeCustomer(() => {
      const card = this.stripeCustomer.getDefaultCard();
      this.setState({
        status: "loaded",
        cardString: card
          ? `${utils.toTitleCase(card.funding)} card, ends with ${card.last4}`
          : undefined,
      });
    });
  }

  render() {
    if (this.state.status === "loading") {
      return <p>Loading...</p>;
    }

    const currentPermissions = models.user.get("subscriptionPlan") as any;

    if (currentPermissions === "freeForever") {
      return (
        <div>
          <h2>Manage Your Premium Plus Subscription</h2>
          <p>
            You have a free lifetime subscription to Premium Plus (which now
            costs $15 / month or $120 / year) for being one of the first 600
            users to sign up to Readlang back in 2013.
          </p>
        </div>
      );
    }

    const months = models.user.get("renewalMonths") ?? 0;
    const price = models.user.get("price");
    const subscriptionExpiryDate = new Date(
      models.user.get("subscriptionExpiryDate")
    );

    const renewalPeriods = [
      subscriptionPlans.getDefaultPlan(currentPermissions, 1),
      subscriptionPlans.getDefaultPlan(currentPermissions, 12),
    ];
    const selectedRenewalPeriodIndex = months === 1 || months === 0 ? 0 : 1;

    const plans = [
      subscriptionPlans.getDefaultPlan("standard", months || 1),
      subscriptionPlans.getDefaultPlan("plus", months || 1),
    ];
    const selectedPlanIndex = currentPermissions === "plus" ? 1 : 0;

    return (
      <div>
        <h2>
          Manage Your Premium{" "}
          {models.user.get("subscriptionPlan") === "plus" ? "Plus" : ""}{" "}
          Subscription
        </h2>
        <table class="table" style={{ fontSize: "100%" }}>
          <tr>
            <th>Plan</th>
            <td>
              {months === 0 ? (
                <select
                  value="auto-renew-disabled"
                  onChange={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    this.selectPlan(event.currentTarget.value);
                  }}
                >
                  <option value="auto-renew-disabled">
                    Premium {currentPermissions === "plus" ? "Plus" : ""}{" "}
                    (Auto-Renewal Disabled)
                  </option>
                  {[
                    subscriptionPlans.getDefaultPlan("standard", 1),
                    subscriptionPlans.getDefaultPlan("standard", 12),
                    subscriptionPlans.getDefaultPlan("plus", 1),
                    subscriptionPlans.getDefaultPlan("plus", 12),
                  ].map((plan) => (
                    <option value={plan.name}>
                      Premium {plan.permissions === "plus" ? "Plus" : ""} ($
                      {plan.price} / {plan.months === 1 ? "month" : "year"})
                    </option>
                  ))}
                </select>
              ) : (
                <select
                  value={plans[selectedPlanIndex].name}
                  onChange={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    this.selectPlan(event.currentTarget.value);
                  }}
                >
                  {plans.map((plan, index) => (
                    <option value={plan.name}>
                      Premium {plan.permissions === "plus" ? "Plus" : ""} ($
                      {index === selectedPlanIndex && price
                        ? price
                        : plan.price}{" "}
                      / {plan.months === 1 ? "month" : "year"})
                    </option>
                  ))}
                </select>
              )}
            </td>
          </tr>
          {months !== 0 ? (
            <tr>
              <th>Renewal Period</th>
              <td>
                <select
                  value={renewalPeriods[selectedRenewalPeriodIndex].name}
                  onChange={(event) => {
                    this.selectPlan(event.currentTarget.value);
                  }}
                >
                  {renewalPeriods.map((plan, index) => (
                    <option value={plan.name}>
                      {
                        {
                          0: "Auto-Renewals Disabled",
                          1: "Monthly",
                          12: "Yearly",
                        }[plan.months]
                      }{" "}
                      ($
                      {index === selectedRenewalPeriodIndex && price
                        ? price
                        : plan.price}
                      )
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          ) : null}
          <tr>
            <th>Payment Method</th>
            <td>{this.state.cardString ?? "No payment method"}</td>
          </tr>
          {this.state.cardString ? (
            <tr>
              <th></th>
              <td>
                <button
                  onClick={() => {
                    window.location.href =
                      "/api/stripe/createCheckoutPaymentMethodSession";
                  }}
                >
                  Change Payment Method
                </button>
              </td>
            </tr>
          ) : null}
          <tr>
            <th>{months === 0 ? "Expires" : "Next Payment Due"}</th>
            <td>
              {subscriptionExpiryDate.toLocaleDateString("en-US", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </td>
          </tr>
          <tr>
            <th></th>
            <td>
              {!models.user.has("renewalMonths") ||
              models.user.get("renewalMonths") === 0 ? (
                ""
              ) : (
                <button
                  onClick={() => {
                    genericModals.confirm({
                      title: "Cancel Subscription?",
                      message:
                        "Are you sure you want to cancel your subscription? (You will still have " +
                        "access to your premium features until the end of your current " +
                        "subscription period.)",
                      callback: (response) => {
                        if (response !== "ok") {
                          this.forceUpdate();
                          return;
                        }
                        this.stripeCustomer.removeSubscription(() => {
                          window.location.reload();
                        });
                      },
                    });
                  }}
                >
                  Cancel Subscription
                </button>
              )}
            </td>
          </tr>
        </table>
      </div>
    );
  }

  selectPlan = (planName: string) => {
    const selectedPlan = subscriptionPlans.plans[planName];

    const months = models.user.get("renewalMonths") ?? 0;
    // const price = models.user.get("price");
    const subscriptionExpiryDate = new Date(
      models.user.get("subscriptionExpiryDate")
    );
    const daysUntilExpiry =
      (subscriptionExpiryDate.getTime() - Date.now()) / (24 * 3600 * 1000);
    const currentPermissions = models.user.get("subscriptionPlan") as any;

    const freeDaysMessage = (switchPlans: boolean) =>
      "You may see a message on the checkout page saying you are getting X " +
      "free days. These are actually just the days you have already paid " +
      `for in your current subscription${
        switchPlans ? " applied to the new plan on a prorated basis" : ""
      }.`;

    const changingPlans = selectedPlan.permissions !== currentPermissions;

    if (months === 0) {
      if (daysUntilExpiry > 0) {
        genericModals.alert({
          message: freeDaysMessage(changingPlans),
          buttons: [{ name: "ok", displayName: "OK" }],
          callback: () => {
            this.createNewSubscription(selectedPlan);
          },
        });
      } else {
        this.createNewSubscription(selectedPlan);
      }
    } else if (changingPlans) {
      genericModals.confirm({
        title: "Change Plan?",
        message:
          "Are you sure you want to change your plan? Since the price of the " +
          "new plan is different, your expiry date will be adjusted accordingly.",
        callback: (response) => {
          if (response !== "ok") {
            this.forceUpdate();
            return;
          }

          // Update existing subscription
          this.stripeCustomer.setPlan(selectedPlan.id, (err) => {
            if (err) {
              alert(
                "There was an error updating your subscription. Please contact steve@readlang.com."
              );
              return;
            }
            window.location.reload();
          });
        },
      });
    } else {
      // Update existing subscription
      this.stripeCustomer.setPlan(selectedPlan.id, (err) => {
        if (err) {
          alert(
            "There was an error updating your subscription. Please contact steve@readlang.com."
          );
          return;
        }
        window.location.reload();
      });
    }
  };

  createNewSubscription(selectedPlan: { months: number; permissions: string }) {
    // Redirect to Stripe checkout
    window.location.href = `/api/createCheckoutSession/${
      selectedPlan.months === 1 ? "month" : "year"
    }?plan=${
      selectedPlan.permissions === "plus" ? "premiumPlus" : "premium"
    }&returnURL=${encodeURIComponent(window.location.href)}`;
  }
}

export default ManageSubscriptionView;
